import { useState, useEffect } from "react";
import { useLang } from "@/contexts/LangContext";
import { Menu, X, Globe, Check, ChevronDown, Briefcase, Plane, UserCircle2, Hotel, TrainFront, Bus } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "@/assets/logo.png";
import { navx } from "@/i18n/navLabels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Travel to China ▾ — existing homepage sections only (no duplicated pages)
const CHINA_ITEMS = [
  { id: "education", key: "educationTrips" },
  { id: "services", key: "medicalTrips" },
  { id: "experiences", key: "chinaExperiences" },
  { id: "visa", key: "visaAssistance" },
];

const Header = () => {
  const { t, lang, setLang, languages } = useLang();
  const { user } = useAuth();
  const nx = navx(lang);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onMainSite = location.pathname === "/";
  const accountTo = user ? (user.role === "super_admin" ? "/admin" : user.role === "customer" ? "/my-journeys" : "/portal") : "/login";
  const accountLabel = user ? (user.role === "super_admin" ? nx.admin : user.role === "customer" ? nx.myJourneys : nx.myPortal) : nx.signIn;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    if (!onMainSite) {
      navigate(`/#${id}`);
      setMobileOpen(false);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F7F5F0]/90 backdrop-blur-xl border-b border-black/5 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => {
            if (onMainSite) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
              navigate("/");
            }
          }}
          className="flex items-center"
          aria-label="The China Explorer — Home"
        >
          <span
            className={`inline-flex items-center justify-center transition-all duration-500 ${
              scrolled
                ? "bg-transparent px-0 py-0"
                : "bg-white/95 px-3 py-1.5 rounded-md shadow-lg shadow-black/10"
            }`}
          >
            <img
              src={logo}
              alt="The China Explorer"
              className="h-10 md:h-12 w-auto"
            />
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          <button
            data-testid="nav-link-destinations"
            onClick={() => scrollTo("destinations")}
            className={`text-sm tracking-wide whitespace-nowrap hover:text-[#C62828] transition-colors ${
              scrolled ? "text-[#0E2A5C]" : "text-white/90"
            }`}
          >
            {t.nav.destinations}
          </button>

          {/* Travel to China ▾ */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-dropdown-china"
                className={`inline-flex items-center gap-1 text-sm tracking-wide whitespace-nowrap hover:text-[#C62828] transition-colors ${
                  scrolled ? "text-[#0E2A5C]" : "text-white/90"
                }`}
              >
                {nx.travelToChina} <ChevronDown size={12} className="opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 mt-2 bg-white border border-black/10 shadow-xl rounded-md p-1" data-testid="china-dropdown-menu">
              <DropdownMenuItem asChild data-testid="china-menu-journeys">
                <Link to="/journeys" className="block px-3 py-2.5 cursor-pointer text-sm font-semibold text-[#0E2A5C] hover:bg-black/5 rounded-sm">
                  ✦ {nx.chinaJourneys}
                </Link>
              </DropdownMenuItem>
              {CHINA_ITEMS.map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  data-testid={`china-menu-${item.id}`}
                  onClick={() => scrollTo(item.id)}
                  className="px-3 py-2.5 cursor-pointer text-sm text-[#121212] hover:bg-black/5 rounded-sm"
                >
                  {nx[item.key]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="nav-link-itinerary"
            onClick={() => scrollTo("itinerary")}
            className={`text-sm tracking-wide whitespace-nowrap hover:text-[#C62828] transition-colors ${
              scrolled ? "text-[#0E2A5C]" : "text-white/90"
            }`}
          >
            {nx.planMyTrip}
          </button>

          {/* Book Travel ▾ — Global Flights primary */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-dropdown-book"
                className={`inline-flex items-center gap-1 text-sm font-semibold tracking-wide whitespace-nowrap transition-colors ${
                  scrolled ? "text-[#0E2A5C] hover:text-[#C62828]" : "text-white hover:text-[#D4AF37]"
                }`}
              >
                {nx.bookTravel} <ChevronDown size={12} className="opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60 mt-2 bg-white border border-black/10 shadow-xl rounded-md p-1" data-testid="book-dropdown-menu">
              <DropdownMenuItem asChild data-testid="book-menu-flights">
                <Link to="/flights" className="flex items-center justify-between px-3 py-3 cursor-pointer text-sm font-semibold text-[#0E2A5C] hover:bg-black/5 rounded-sm">
                  <span className="inline-flex items-center gap-2"><Plane size={14} className="text-[#D4AF37]" /> {nx.globalFlights}</span>
                  <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-white bg-[#C62828] px-1.5 py-0.5 rounded-sm">{nx.live}</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem disabled data-testid="book-menu-hotels" className="flex items-center justify-between px-3 py-2.5 text-sm text-[#4A4A4A] rounded-sm opacity-70">
                <span className="inline-flex items-center gap-2"><Hotel size={14} /> {nx.globalHotels}</span>
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#D4AF37]">{nx.comingSoon}</span>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="book-menu-rail">
                <Link to="/flights#more-ways" className="flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm text-[#121212] hover:bg-black/5 rounded-sm">
                  <TrainFront size={14} /> {nx.rail}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="book-menu-bus">
                <Link to="/flights#more-ways" className="flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm text-[#121212] hover:bg-black/5 rounded-sm">
                  <Bus size={14} /> {nx.bus}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/china-business-explorer"
            data-testid="nav-link-business-travel"
            className={`inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide whitespace-nowrap px-3.5 py-1.5 rounded-full transition-colors ${
              scrolled
                ? "bg-[#C62828] text-white hover:bg-[#B71C1C]"
                : "bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#bf9a2b]"
            }`}
          >
            <Briefcase size={13} /> {nx.businessTravel}
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to={accountTo}
            data-testid="nav-account-link"
            className={`hidden md:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-xs uppercase tracking-[0.12em] font-semibold whitespace-nowrap transition-colors ${
              scrolled
                ? "border-[#0E2A5C]/40 text-[#0E2A5C] hover:border-[#0E2A5C]"
                : "border-white/40 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]"
            }`}
          >
            <UserCircle2 size={14} /> {accountLabel}
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switcher-button"
                className={`hidden md:inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm transition-colors ${
                  scrolled
                    ? "border-black/15 text-[#0E2A5C] hover:border-[#0E2A5C]"
                    : "border-white/30 text-white hover:border-white"
                }`}
                aria-label="Change language"
              >
                <Globe size={14} />
                <span className="uppercase tracking-wider text-xs">
                  {languages[lang].label}
                </span>
                <ChevronDown size={12} className="opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              data-testid="lang-switcher-menu"
              className="w-44 mt-2 bg-white border border-black/10 shadow-xl rounded-md p-1"
            >
              {Object.entries(languages).map(([code, info]) => (
                <DropdownMenuItem
                  key={code}
                  data-testid={`lang-switcher-${code}`}
                  onClick={() => setLang(code)}
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm rounded-sm ${
                    lang === code
                      ? "bg-[#C62828]/10 text-[#C62828]"
                      : "text-[#121212] hover:bg-black/5"
                  }`}
                >
                  <span>{info.name}</span>
                  {lang === code && <Check size={14} />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${scrolled ? "text-[#0E2A5C]" : "text-white"}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F7F5F0] border-t border-black/5 px-6 py-6 space-y-4"
        >
          <button
            data-testid="mobile-nav-link-destinations"
            onClick={() => scrollTo("destinations")}
            className="block text-left w-full text-base text-[#0E2A5C] hover:text-[#C62828] transition-colors"
          >
            {t.nav.destinations}
          </button>

          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#4A4A4A] mb-2">{nx.travelToChina}</p>
            <div className="pl-3 border-l border-black/10 space-y-2.5">
              <Link to="/journeys" data-testid="mobile-china-journeys" onClick={() => setMobileOpen(false)}
                className="block text-sm font-semibold text-[#0E2A5C] hover:text-[#C62828] transition-colors">✦ {nx.chinaJourneys}</Link>
              {CHINA_ITEMS.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-china-${item.id}`}
                  onClick={() => scrollTo(item.id)}
                  className="block text-left w-full text-sm text-[#0E2A5C] hover:text-[#C62828] transition-colors"
                >
                  {nx[item.key]}
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="mobile-nav-link-itinerary"
            onClick={() => scrollTo("itinerary")}
            className="block text-left w-full text-base text-[#0E2A5C] hover:text-[#C62828] transition-colors"
          >
            {nx.planMyTrip}
          </button>

          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#4A4A4A] mb-2">{nx.bookTravel}</p>
            <div className="pl-3 border-l border-black/10 space-y-2.5">
              <Link
                to="/flights"
                data-testid="mobile-nav-link-flights"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 text-sm font-semibold text-[#0E2A5C] hover:text-[#C62828] transition-colors"
              >
                <Plane size={14} className="text-[#D4AF37]" /> {nx.globalFlights}
              </Link>
              <p className="flex items-center gap-2 text-sm text-[#4A4A4A]" data-testid="mobile-nav-hotels">
                <Hotel size={14} /> {nx.globalHotels} <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-[#D4AF37]">{nx.comingSoon}</span>
              </p>
              <Link to="/flights#more-ways" data-testid="mobile-nav-rail" onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 text-sm text-[#0E2A5C] hover:text-[#C62828] transition-colors">
                <TrainFront size={14} /> {nx.rail}
              </Link>
              <Link to="/flights#more-ways" data-testid="mobile-nav-bus" onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 text-sm text-[#0E2A5C] hover:text-[#C62828] transition-colors">
                <Bus size={14} /> {nx.bus}
              </Link>
            </div>
          </div>

          <Link
            to="/china-business-explorer"
            data-testid="mobile-nav-link-business-travel"
            onClick={() => setMobileOpen(false)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C62828] text-white text-sm font-semibold"
          >
            <Briefcase size={13} /> {nx.businessTravel}
          </Link>
          <Link
            to={accountTo}
            data-testid="mobile-nav-account-link"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 text-left w-full text-base font-semibold text-[#0E2A5C] hover:text-[#C62828] transition-colors"
          >
            <UserCircle2 size={15} /> {accountLabel}
          </Link>
          <div className="flex flex-wrap gap-2 pt-2">
            {Object.entries(languages).map(([code, info]) => (
              <button
                key={code}
                data-testid={`mobile-lang-${code}`}
                onClick={() => setLang(code)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full ${
                  lang === code
                    ? "bg-[#C62828] text-white"
                    : "border border-black/20 text-[#4A4A4A]"
                }`}
              >
                {info.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
